'use client';

/**
 * InviteUserModal Component
 *
 * Modal for inviting a new user to an organization.
 * Sends invitation email with role assignment.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@cortex/ui';
import { Mail } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { useApi } from '@/hooks/use-api';

interface InviteUserModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Organization ID to invite user to */
  organizationId: string;
  /** Callback when user is successfully invited */
  onSuccess?: () => void;
}

const inviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['user', 'admin']),
});

export function InviteUserModal({
  open,
  onClose,
  organizationId,
  onSuccess,
}: InviteUserModalProps) {
  const getApi = useApi();
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<'user' | 'admin'>('user');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<{
    email?: string;
  }>({});

  React.useEffect(() => {
    if (!open) {
      setEmail('');
      setRole('user');
      setLoading(false);
      setError(null);
      setValidationErrors({});
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    setValidationErrors({});

    const result = inviteSchema.safeParse({ email, role });
    if (!result.success) {
      const errors: { email?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'email') {
          errors.email = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const api = await getApi();
      const response = await api.invitations.index.$post({
        json: {
          email,
          organizationId,
          role,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          ('error' in data && typeof data.error === 'string'
            ? data.error
            : null) || 'Failed to send invitation'
        );
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation to join this organization
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Stack gap="xs">
              <Label htmlFor="inviteEmail">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!validationErrors.email}
                errorMessage={validationErrors.email || ''}
                leftAddon={<Mail className="h-4 w-4" />}
                disabled={loading}
              />
              <Text color="muted" size="xs" className="mt-1">
                They will receive an email with a link to join
              </Text>
            </Stack>

            <Stack gap="xs">
              <Label htmlFor="inviteRole">Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as 'user' | 'admin')}
                disabled={loading}
              >
                <SelectTrigger id="inviteRole">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <Stack gap="none">
                      <Text weight="medium">User</Text>
                      <Text className="text-foreground/60 text-sm">
                        Can view and create projects
                      </Text>
                    </Stack>
                  </SelectItem>
                  <SelectItem value="admin">
                    <Stack gap="none">
                      <Text weight="medium">Admin</Text>
                      <Text className="text-foreground/60 text-sm">
                        Full organization access
                      </Text>
                    </Stack>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Text color="muted" size="xs" className="mt-1">
                Determines what the user can do in the organization
              </Text>
            </Stack>

            <Card className="bg-surface-2 p-3">
              <Text size="sm" color="muted">
                The user will be able to accept the invitation within 7 days.
                After that, you&apos;ll need to send a new invitation.
              </Text>
            </Card>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!email} loading={loading}>
            Send Invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
